import React, { useEffect, useMemo, useState, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  getDatabase,
  ref,
  onValue,
  off,
} from 'firebase/database';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { AppContext } from './AppContext';

const TokenBlockPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useContext(AppContext);

  const [block, setBlock] = useState(null);

  // BUY modal state
  const [mode, setMode] = useState(null); // 'buy' | null
  const [qtyInput, setQty] = useState('');
  const [error, setError] = useState('');
  const [isBuying, setIsBuying] = useState(false);

  // Load this block
  useEffect(() => {
    const db = getDatabase();
    const blockRef = ref(db, `tokenBlocks/${id}`);

    const unsubBlock = onValue(
      blockRef,
      snap => {
        if (!snap.exists()) { setBlock(null); return; }
        const v = snap.val();
        setBlock({
          id,
          price: Number(v?.price || 0),
          platformRemaining: Number(v?.platformRemaining || 0),
          walletId: v?.walletId || null,
        });
      },
      err => {
        console.error('💥 block listener error', err);
        setBlock(null);
      }
    );

    return () => {
      off(blockRef, 'value', unsubBlock);
    };
  }, [id]);

  // Minimum USD purchase unless this block has < $25 left in total
  const MIN_PURCHASE_USD = 10;

  const buyLimits = useMemo(() => {
    if (!block) return null;
    const price = Number(block.price || 0);
    const remaining = Number(block.platformRemaining || 0);
const remainingValue = price * remaining;

// Always enforce $25 minimum; webhook will bleed into next block if needed
const isTinyRemainder = false;
const minTokens = Math.ceil(MIN_PURCHASE_USD / price);
const maxTokens = remaining;

// For display only
const minSpendDisplay = minTokens * price;

return { price, remaining, remainingValue, isTinyRemainder, minTokens, maxTokens, minSpendDisplay };

  }, [block]);

  const openBuy = () => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    if (!block || isBuying) return;
    setMode('buy');
    setError('');
    // Prefill qty to the min required (or 1 if tiny remainder)
    setTimeout(() => {
      setQty(String(buyLimits?.minTokens ?? 1));
    }, 0);
  };

  

  // Clear any "opening Stripe" state when we return to this tab/page
  useEffect(() => {
    const clearStripeState = () => {
      setIsBuying(false);
      if (sessionStorage.getItem('stripeRedirect')) {
        sessionStorage.removeItem('stripeRedirect');
      }
    };
    const onPageShow = () => clearStripeState();
    const onVis = () => { if (!document.hidden) clearStripeState(); };
    if (sessionStorage.getItem('stripeRedirect')) clearStripeState();
    window.addEventListener('pageshow', onPageShow);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      window.removeEventListener('pageshow', onPageShow);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const confirmBuy = async () => {
    if (!block || isBuying) return;

    const qty = parseInt(qtyInput, 10);
    if (!qty || qty <= 0) { setError('Enter a positive number'); return; }

    // Enforce remaining cap
    if (buyLimits && qty > buyLimits.maxTokens) {
      setError(`Only ${buyLimits.maxTokens.toLocaleString()} tokens remain in this block`);
      return;
    }

// Enforce $10 minimum (bleed-over handled in webhook)
if (buyLimits) {
  const spend = qty * buyLimits.price;
  if (spend < MIN_PURCHASE_USD) {
    setError(`Minimum purchase is $${MIN_PURCHASE_USD} (${buyLimits.minTokens.toLocaleString()} tokens)`);
    return;
  }
}


    try {
      setIsBuying(true);
      setError('');

      const functions = getFunctions();
      const createLink = httpsCallable(functions, 'createPaymentLinkDatTokenTesting');
      const res = await createLink({ blockId: block.id, qty });

      const url = res?.data?.url;
      if (!url) throw new Error('Stripe link not returned');

      sessionStorage.setItem('stripeRedirect', '1');
      window.location.assign(url);
      setMode(null);
    } catch (err) {
      console.error(err);
      setError(err?.message || 'Purchase failed');
      setIsBuying(false);
    }
  };

  const renderModal = () => {
    if (mode !== 'buy') return null;

    return (
      <div className="modal-overlay" onClick={() => { if (!isBuying) setMode(null); }}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
          <h2>Purchase Tokens</h2>

          {block && (
            <>
              <p className="modal-price">
                Price: <strong>${block.price.toFixed(2)}</strong>
              </p>
              <p className="modal-remaining">
                {Number(block.platformRemaining || 0).toLocaleString()} available
              </p>

{buyLimits && (
  <p className="price-range">
    {`Minimum: ${buyLimits.minTokens.toLocaleString()} tokens ($${buyLimits.minSpendDisplay.toFixed(2)})`}
  </p>
)}

            </>
          )}

          <input
            type="number"
            min={buyLimits?.minTokens ?? 1}
            max={buyLimits?.maxTokens ?? undefined}
            step="10"
            value={qtyInput}
            onChange={e => setQty(e.target.value)}
            placeholder="Quantity"
            className="modal-input"
          />

          {error && <p className="modal-error">{error}</p>}

          <div className="modal-actions">
            <button
              className="modal-btn cancel"
              onClick={() => setMode(null)}
              disabled={isBuying}
            >
              Cancel
            </button>
            <button
              className="modal-btn confirm"
              onClick={confirmBuy}
              disabled={isBuying}
            >
              Confirm
            </button>
          </div>
        </div>
      </div>
    );
  };

  const totalBlockAvailable = Number(block?.platformRemaining || 0);

  return (
    <div className="tbp-wrap">
      <div className="tbp-header">
        <h1>Link {id}</h1>
      </div>

      <div className="tbp-section">
        <div className="tbp-tdc-box">
          <div className="tdc-amount">
            Total Available {totalBlockAvailable.toLocaleString()}
          </div>
          <div className="tdc-caption"></div>
        </div>
      </div>

      {block ? (
        <>
          <div className="tbp-card">
            <div className="tbp-row">
              <div className="tbp-label">Price</div>
              <div className="tbp-value">
                ${block.price.toFixed(2)} per token
              </div>
            </div>
          </div>

          <div className="tbp-actions">
            <button
              className="purchase-btn"
              onClick={openBuy}
              disabled={!block || isBuying || (buyLimits?.maxTokens ?? 0) <= 0}
            >
              BUY TOKENS
            </button>
          </div>
        </>
      ) : (
        <div className="tbp-empty">Loading block data…</div>
      )}

      {renderModal()}

      {isBuying && (
        <div className="blocking-overlay" role="dialog" aria-modal="true">
          <div className="blocking-content">
            <div className="spinner" />
            <div className="blocking-text">Opening Stripe…</div>
          </div>
        </div>
      )}

      <style>{`
  :root{
    --brand-top:   #fe24ad;  /* pink */
    --brand-bot:   #a410e8;  /* purple */
    --brand-grad:  linear-gradient(135deg, var(--brand-top), var(--brand-bot));
    --brand-top-20: rgba(254,36,173,.2);
    --brand-bot-40: rgba(164,16,232,.4);
    --brand-bot-25: rgba(164,16,232,.25);
  }

  *{margin:0;padding:0;box-sizing:border-box;}
  html,body{font-family:sans-serif;background:#000;color:#fff;}

  .tbp-wrap{
    min-height:100vh;
    padding:1.25rem;
    background: radial-gradient(1200px 800px at 15% 10%, var(--brand-top-20), transparent 60%),
                radial-gradient(900px 700px at 85% 90%, var(--brand-bot-25), transparent 60%),
                #000;
    color:#fff;
  }

  .tbp-header{display:flex;align-items:center;gap:.75rem;margin-bottom:1rem;}
  h1{font-size:1.4rem;margin:0}
  h2{font-size:1.1rem;margin:.75rem 0 .4rem;color:#fff}

  .tbp-card{
    background:#050008;
    border:1px solid var(--brand-bot-40);
    border-radius:12px;padding:1rem;margin-bottom:1rem;
    box-shadow:0 0 12px rgba(164,16,232,.15) inset;
  }
  .tbp-row{
    display:flex;justify-content:space-between;
    padding:.35rem 0;border-bottom:1px dashed var(--brand-top-20);
  }
  .tbp-row:last-child{border-bottom:none}
  .tbp-label{opacity:.85}
  .tbp-value{
    font-weight:800;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }

  .tbp-section{
    background:#050008;
    border:1px solid var(--brand-bot-40);
    border-radius:12px;padding:1rem;margin-bottom:1rem;
  }

  .tbp-tdc-box{
    display:flex;align-items:baseline;gap:.5rem;
    background:#07000c;border:1px solid var(--brand-bot-40);
    border-radius:10px;padding:.85rem 1rem;
    box-shadow:inset 0 0 24px rgba(254,36,173,.08);
  }
  .tdc-amount{
    font-size:1.4rem;font-weight:900;
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }
  .tdc-caption{opacity:.85}

.tbp-actions {
  display: flex;
  justify-content: center;   /* center horizontally */
  margin: 1.5rem 0;          /* a bit more spacing around */
}

.purchase-btn {
  padding: 0.8rem 2.5rem;    /* make it taller & wider */
  min-width: 240px;          /* enforce width */
  text-align: center;
  border: none;
  border-radius: 10px;
  font-weight: 700;
  font-size: 1rem;
  cursor: pointer;
  background: var(--brand-grad);
  color: #fff;
  transition: filter 0.2s ease;
}

.purchase-btn:disabled {
  background: var(--brand-top-20);
  color: rgba(255,255,255,.5);
  cursor: not-allowed;
}


  /* MODAL */
  .modal-overlay{
    position:fixed;
    inset:0;
    background:rgba(0,0,0,.65);
    display:flex;
    align-items:center;
    justify-content:center;
    z-index:999;
  }
  .modal-content{
    background:#000;
    padding:2rem;
    border-radius:12px;
    max-width:400px;
    width:90%;
    border:1px solid var(--brand-bot-40);
    box-shadow:0 0 20px rgba(254,36,173,.15);
  }
  .modal-content h2{margin-top:0;color:#fff;}
  .modal-price strong,
  .modal-remaining,
  .price-range strong{
    background:var(--brand-grad);
    -webkit-background-clip:text;
    -webkit-text-fill-color:transparent;
  }

  .modal-input{
    width:100%;
    padding:.6rem .8rem;
    margin:.6rem 0;
    border:1px solid var(--brand-bot-40);
    border-radius:6px;
    background:#050008;
    color:#fff;
  }
  .modal-input:focus{
    outline:none;
    border-color:var(--brand-top);
    box-shadow:0 0 6px var(--brand-top);
  }

  .modal-error{color:var(--brand-top);font-size:.9rem;}
  .price-range{font-size:.85rem;color:#ccc;margin:.3rem 0;}

  .modal-actions{
    display:flex;
    justify-content:flex-end;
    gap:.75rem;
    margin-top:1rem;
  }
  .modal-btn{
    padding:.5rem 1.2rem;
    border:none;
    border-radius:6px;
    font-weight:600;
    cursor:pointer;
    transition:filter .2s ease;
  }
  .modal-btn.cancel{
    background:var(--brand-top-20);
    color:#fff;
  }
  .modal-btn.cancel:disabled{
    background:rgba(255,255,255,.1);
    cursor:not-allowed;
  }
  .modal-btn.confirm{
    background:var(--brand-grad);
    color:#fff;
  }
  .modal-btn.confirm:disabled{
    background:var(--brand-top-20);
    color:rgba(255,255,255,.5);
    cursor:not-allowed;
  }

  /* BLOCKING OVERLAY DURING ACTION */
  .blocking-overlay{
    position:fixed;
    inset:0;
    background:rgba(0,0,0,.8);
    display:flex;
    align-items:center;
    justify-content:center;
    z-index:1001;
  }
  .blocking-content{
    background:#050008;
    color:#fff;
    padding:1.25rem 1.5rem;
    border-radius:10px;
    min-width:220px;
    display:flex;
    flex-direction:column;
    align-items:center;
    gap:.75rem;
    box-shadow:0 10px 30px rgba(0,0,0,.45);
    border:1px solid var(--brand-bot-40);
  }
  .spinner{
    width:38px;
    height:38px;
    border:4px solid rgba(255,255,255,.15);
    border-top-color:var(--brand-top);
    border-radius:50%;
    animation:spin 1s linear infinite;
  }
  .blocking-text{font-weight:700;letter-spacing:.3px;}
  @keyframes spin{to{transform:rotate(360deg);}}
      `}</style>
    </div>
  );
};

export default TokenBlockPage;
